import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import {
  DropdownButton,
  Flex,
  IconEditFormOutline,
  IconFavoriteHomeOutline,
  IconHideOutline,
  Menu,
  MenuItem,
  MenuPopper,
} from '@zillow/constellation';
import { listingAliasSelector } from '../../../../selectors';
import { getDeclineApplicationPath, getLeasesPath } from '../../../../constants/urls';
import {
  getIsAcceptedOrInvitedToMoveIn,
  getIsDeclined,
  INVITE_TO_MOVE_IN_QUERY,
} from '../../../../utils/inviteToMoveIn';
import useQueryVariables from '../../../../hooks/useQueryVariables';
import TakeActionTooltip from '../TakeActionTooltip';
import { Link } from 'react-router-dom';
import { useListingUser } from '../../../../hooks/useListingUser';
import {
  emitTakeActionMenuApproveApplicationClickedEvent,
  emitTakeActionMenuCreateLeaseClickedEvent,
  emitTakeActionMenuDeclineEvent,
  emitTakeActionMenuOpenedEvent,
} from '../../../../../client/analytics/clickstreamEvents';

function TakeActionDropdown({ onApproveApplicationClickCallback }) {
  const listingAlias = useSelector(listingAliasSelector);
  const { user, listing } = useListingUser();
  const { applicationId, propertyId } = useQueryVariables();
  const { data } = useQuery(INVITE_TO_MOVE_IN_QUERY, { variables: { propertyId, applicationId } });
  const isAcceptedOrInvitedToMoveIn = getIsAcceptedOrInvitedToMoveIn(data);
  const isApplicationDeclined = getIsDeclined(data);

  const onApproveApplicationClick = () => {
    emitTakeActionMenuApproveApplicationClickedEvent(listing, user);
    onApproveApplicationClickCallback();
  };

  const takeActionMenu = (
    <Menu>
      {
        <>
          {!isAcceptedOrInvitedToMoveIn && !isApplicationDeclined && (
            <MenuItem
              icon={<IconFavoriteHomeOutline />}
              onClick={onApproveApplicationClick}
              data-test="approve-application-menu-item"
            >
              Approve application
            </MenuItem>
          )}
          {!isApplicationDeclined && (
            <MenuItem
              as={Link}
              to={getDeclineApplicationPath(applicationId, propertyId)}
              relative="path"
              icon={<IconHideOutline />}
              onClick={() => emitTakeActionMenuDeclineEvent(listing, user)}
              data-test="decline-application-menu-item"
            >
              Decline application
            </MenuItem>
          )}
          {!isApplicationDeclined && (
            <MenuItem
              as="a"
              icon={<IconEditFormOutline />}
              href={getLeasesPath(listingAlias, applicationId)}
              onClick={() => emitTakeActionMenuCreateLeaseClickedEvent(listing, user)}
              data-test="create-lease-menu-item"
            >
              Create a lease
            </MenuItem>
          )}
        </>
      }
    </Menu>
  );

  return (
    <Flex
      display="flex"
      flexDirection={{ default: 'row-reverse', lg: 'row' }}
      alignItems="top"
      justifyContent="flex-end"
      marginTop={{ default: 'sm', lg: 'unset' }}
      gap="xxs"
    >
      <TakeActionTooltip />
      <div className="zon_TakeActionDropdown">
        <Flex display="flex" alignItems={{ default: 'flex-start', lg: 'center' }}>
          <MenuPopper buttonType="primary" triggered={takeActionMenu}>
            <DropdownButton onClick={() => emitTakeActionMenuOpenedEvent(listing, user)}>Take action</DropdownButton>
          </MenuPopper>
        </Flex>
      </div>
    </Flex>
  );
}

TakeActionDropdown.propTypes = {
  onApproveApplicationClickCallback: PropTypes.func.isRequired,
};

export default TakeActionDropdown;
